import { useState } from "react";
import { Link } from "react-router-dom";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Input, PageHeader, Pill, SortableTh, Table, Td, Th } from "@/components/ui";
import { PaginationBottom, PaginationTop } from "@/components/Pagination";

interface User {
  id: number;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  created_at: string;
  is_blocked: boolean;
  used_test: boolean; // пробный VPN уже использован
  test_expires_at: string | null;
  paid_expires_at: string | null;
  proxy_state: "none" | "active" | "expired";
  proxy_plan: "paid" | "trial" | null;
  proxy_expires_at: string | null;
}

interface UsersListResponse {
  users: User[];
  total: number;
}

interface UsersSearchResponse {
  users: User[];
}

type SortCol = "id" | "created_at" | "test_expires_at" | "paid_expires_at";
type SortDir = "asc" | "desc";
type Filter =
  | "all"
  | "active_paid"
  | "active_test"
  | "paid_expired"
  | "test_expired"
  | "no_test"
  | "blocked";

const FILTER_LABELS: { key: Filter; label: string }[] = [
  { key: "all", label: "Все" },
  { key: "active_paid", label: "Активный paid" },
  { key: "active_test", label: "Активный test" },
  { key: "paid_expired", label: "Истёкший paid" },
  { key: "test_expired", label: "Истёкший test" },
  { key: "no_test", label: "Не брал тест" },
  { key: "blocked", label: "Заблокированные" },
];

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });

/** Осталось дней до даты — чтобы не считать в уме. */
function daysLeft(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

const AVATAR_TONES = [
  "bg-sky-500/15 text-sky-300",
  "bg-emerald-500/15 text-emerald-300",
  "bg-violet-500/15 text-violet-300",
  "bg-amber-500/15 text-amber-300",
  "bg-rose-500/15 text-rose-300",
  "bg-teal-500/15 text-teal-300",
];

function Avatar({ user }: { user: User }) {
  const label = (user.first_name || user.username || "?").trim().charAt(0).toUpperCase();
  const tone = AVATAR_TONES[user.id % AVATAR_TONES.length];
  return (
    <span
      className={`w-8 h-8 shrink-0 rounded-full grid place-items-center text-[13px] font-semibold ${tone}`}
    >
      {label}
    </span>
  );
}

export default function Users() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState<SortCol | null>(null);
  const [dir, setDir] = useState<SortDir>("desc");
  const [filter, setFilter] = useState<Filter>("all");
  const [vpnFilter, setVpnFilter] = useState<"all" | "has" | "active" | "none">("all");
  const [copied, setCopied] = useState<number | null>(null);
  const qc = useQueryClient();

  const offset = (page - 1) * pageSize;

  const toggleSort = (col: string) => {
    const key = col as SortCol;
    if (sort !== key) {
      setSort(key);
      setDir("desc");
    } else if (dir === "desc") {
      setDir("asc");
    } else {
      setSort(null);
      setDir("desc");
    }
    setPage(1);
  };

  const listUrl = `/api/admin/users?limit=${pageSize}&offset=${offset}&filter=${filter}&vpn=${vpnFilter}${
    sort ? `&sort=${sort}&direction=${dir}` : ""
  }`;

  const list = useQuery<UsersListResponse>({
    queryKey: ["users", "list", page, pageSize, sort, dir, filter, vpnFilter],
    queryFn: () => api(listUrl),
    enabled: q.trim() === "",
    placeholderData: keepPreviousData,
  });

  const search = useQuery<UsersSearchResponse>({
    queryKey: ["users", "search", q.trim()],
    queryFn: () => api(`/api/admin/users/search?q=${encodeURIComponent(q.trim())}`),
    enabled: q.trim() !== "",
    placeholderData: keepPreviousData,
  });

  const users = q.trim() ? (search.data?.users ?? []) : (list.data?.users ?? []);
  const total = list.data?.total ?? 0;

  const tgIds = users.map((u) => u.telegram_id);
  const vpnBatch = useQuery<{
    items: { telegram_id: number; exists: boolean; active: boolean }[];
    available: boolean;
  }>({
    queryKey: ["vpn-batch", tgIds.join(",")],
    queryFn: () =>
      api("/api/admin/users/vpn-batch", {
        method: "POST",
        body: JSON.stringify({ telegram_ids: tgIds }),
      }),
    enabled: tgIds.length > 0,
    staleTime: 60000,
  });
  const vpnMap = new Map((vpnBatch.data?.items ?? []).map((i) => [i.telegram_id, i]));
  const firstLoad = q.trim() ? search.isLoading && !search.data : list.isLoading && !list.data;
  const fetching = q.trim() ? search.isFetching : list.isFetching;

  const toggleBlock = useMutation({
    mutationFn: (u: User) =>
      api(`/api/admin/users/${u.id}/${u.is_blocked ? "unblock" : "block"}`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const copyId = (tid: number) => {
    navigator.clipboard?.writeText(String(tid));
    setCopied(tid);
    setTimeout(() => setCopied((c) => (c === tid ? null : c)), 1200);
  };

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Пользователи"
        right={
          <div className="text-zinc-400 text-sm tabular-nums">
            {q.trim() ? `найдено: ${users.length}` : `всего: ${total.toLocaleString("ru-RU")}`}
          </div>
        }
      />

      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <Input
          placeholder="Поиск по telegram_id или @username"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="w-full sm:w-80"
        />
        {!q.trim() && total > 0 && (
          <PaginationTop
            total={total}
            pageSize={pageSize}
            onPageSizeChange={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        )}
      </div>

      {!q.trim() && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06] flex-wrap">
            {FILTER_LABELS.map((f) => (
              <button
                key={f.key}
                onClick={() => {
                  setFilter(f.key);
                  setPage(1);
                }}
                className={`h-7 px-3 rounded-md text-xs font-medium transition-all ${
                  filter === f.key
                    ? "bg-brand-soft text-brand glow-ring"
                    : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-white/[0.03] border border-white/[0.06]">
            <span className="px-2 text-[11px] text-zinc-600 uppercase tracking-wide">VPN</span>
            {(
              [
                ["all", "Все"],
                ["has", "Есть"],
                ["active", "Активный"],
                ["none", "Без VPN"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                onClick={() => {
                  setVpnFilter(key);
                  setPage(1);
                }}
                className={`h-7 px-3 rounded-md text-xs font-medium transition-all ${
                  vpnFilter === key
                    ? "bg-brand-soft text-brand glow-ring"
                    : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {firstLoad ? (
        <TableSkeleton rows={pageSize} />
      ) : users.length === 0 ? (
        <div className="text-zinc-500">Ничего не найдено.</div>
      ) : (
        <>
          <div className={fetching ? "opacity-60 transition-opacity" : "transition-opacity"}>
            <Table>
              <thead>
                <tr>
                  <SortableTh sortKey="id" currentSort={sort} currentDir={dir} onSort={toggleSort}>
                    Пользователь
                  </SortableTh>
                  <SortableTh
                    sortKey="paid_expires_at"
                    currentSort={sort}
                    currentDir={dir}
                    onSort={toggleSort}
                  >
                    Прокси
                  </SortableTh>
                  <Th>VPN</Th>
                  <SortableTh
                    sortKey="created_at"
                    currentSort={sort}
                    currentDir={dir}
                    onSort={toggleSort}
                  >
                    Регистрация
                  </SortableTh>
                  <Th align="right" fit>
                    Действия
                  </Th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className={`group hover:bg-white/[0.03] transition-colors ${
                      u.is_blocked ? "opacity-55" : ""
                    }`}
                  >
                    <Td>
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar user={u} />
                        <div className="min-w-0">
                          <Link
                            to={`/users/${u.id}`}
                            className="block truncate text-zinc-100 hover:text-brand font-medium"
                          >
                            {u.first_name || (u.username ? `@${u.username}` : `#${u.id}`)}
                            {u.is_blocked && (
                              <span className="ml-2 align-middle">
                                <Pill tone="danger">заблокирован</Pill>
                              </span>
                            )}
                          </Link>
                          <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                            {u.username && <span className="truncate">@{u.username}</span>}
                            <button
                              onClick={() => copyId(u.telegram_id)}
                              title="Скопировать telegram_id"
                              className="font-mono hover:text-zinc-300 transition-colors"
                            >
                              {copied === u.telegram_id ? "скопировано" : u.telegram_id}
                            </button>
                          </div>
                        </div>
                      </div>
                    </Td>
                    <Td>{renderProxy(u)}</Td>
                    <Td>{renderVpn(u, vpnMap.get(u.telegram_id), vpnBatch.data?.available)}</Td>
                    <Td className="text-zinc-500 tabular-nums">{shortDate(u.created_at)}</Td>
                    <Td align="right" fit>
                      <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                        <Link to={`/users/${u.id}`}>
                          <Button variant="ghost">Открыть</Button>
                        </Link>
                        <Button
                          variant={u.is_blocked ? "success" : "danger"}
                          onClick={() => toggleBlock.mutate(u)}
                        >
                          {u.is_blocked ? "Разблок" : "Блок"}
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {!q.trim() && total > pageSize && (
            <PaginationBottom total={total} page={page} pageSize={pageSize} onPageChange={setPage} />
          )}
        </>
      )}
    </div>
  );
}

/** Прокси одной ячейкой. Различаем три состояния явно: не было / активен / истёк.
 *  trial-план = общий пробный период ИЛИ дни за друзей (продлевают ту же строку). */
function renderProxy(u: User) {
  if (u.proxy_state === "none") {
    return <span className="text-xs text-zinc-700">не было</span>;
  }
  const plan = u.proxy_plan === "trial" ? "пробный" : "платный";
  const exp = u.proxy_expires_at;
  if (u.proxy_state === "expired") {
    return (
      <div className="flex items-center gap-2">
        <Pill tone="neutral">истёк</Pill>
        <span className="text-xs text-zinc-600 tabular-nums">
          {plan}
          {exp ? ` · ${shortDate(exp)}` : ""}
        </span>
      </div>
    );
  }
  const d = exp ? daysLeft(exp) : null;
  return (
    <div className="flex items-center gap-2">
      <Pill tone={d !== null && d <= 3 ? "warning" : "success"}>{plan}</Pill>
      <span className="text-xs text-zinc-500 tabular-nums">
        {d === null ? "бессрочно" : `${d} дн.`}
        {exp ? ` · ${shortDate(exp)}` : ""}
      </span>
    </div>
  );
}

/** VPN: не было / активен / отключён. used_test — пробный VPN уже потрачен. */
function renderVpn(
  u: User,
  v: { exists: boolean; active: boolean } | undefined,
  available: boolean | undefined,
) {
  if (available === false) return <span className="text-zinc-700">—</span>;
  if (!v) return <span className="text-zinc-700">·</span>;
  if (!v.exists) {
    return (
      <span className="text-xs text-zinc-700">
        {u.used_test ? "пробный потрачен" : "не было"}
      </span>
    );
  }
  return v.active ? (
    <Pill tone="success">активен</Pill>
  ) : (
    <div className="flex items-center gap-2">
      <Pill tone="danger">отключён</Pill>
    </div>
  );
}

function TableSkeleton({ rows }: { rows: number }) {
  return (
    <div className="rounded-2xl border border-white/[0.07] overflow-hidden card-elev">
      <div className="bg-white/[0.03] h-10 border-b border-white/[0.06]" />
      <div className="divide-y divide-white/[0.04]">
        {Array.from({ length: Math.min(rows, 15) }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3 animate-pulse">
            <div className="h-8 w-8 bg-zinc-800/70 rounded-full" />
            <div className="space-y-1.5">
              <div className="h-3.5 w-32 bg-zinc-800/70 rounded" />
              <div className="h-2.5 w-24 bg-zinc-800/50 rounded" />
            </div>
            <div className="h-4 w-24 bg-zinc-800/60 rounded ml-6" />
            <div className="h-4 w-20 bg-zinc-800/50 rounded" />
            <div className="h-4 w-16 bg-zinc-800/40 rounded ml-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}
